export function normalizeIban(value: string | null | undefined): string {
  return String(value ?? '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');
}

const IBAN_LENGTH_BY_COUNTRY: Record<string, number> = {
  DE: 22,
  AT: 20,
  CH: 21,
  NL: 18,
  FR: 27,
  BE: 16,
  LU: 20,
  IT: 27,
  ES: 24,
  PL: 28,
};

export function isValidIban(value: string | null | undefined): boolean {
  const normalized = normalizeIban(value);
  if (!normalized) return false;
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]+$/.test(normalized)) return false;

  const country = normalized.slice(0, 2);
  const expectedLength = IBAN_LENGTH_BY_COUNTRY[country];
  if (expectedLength && normalized.length !== expectedLength) return false;
  if (!expectedLength && (normalized.length < 15 || normalized.length > 34)) return false;

  const rearranged = `${normalized.slice(4)}${normalized.slice(0, 4)}`;
  let remainder = 0;
  for (const ch of rearranged) {
    const asDigits = ch >= 'A' && ch <= 'Z' ? String(ch.charCodeAt(0) - 55) : ch;
    for (const digit of asDigits) {
      remainder = (remainder * 10 + Number(digit)) % 97;
    }
  }
  return remainder === 1;
}

export function formatIbanDisplay(value: string | null | undefined): string {
  const normalized = normalizeIban(value);
  return normalized.replace(/(.{4})/g, '$1 ').trim();
}

export function normalizeBic(value: string | null | undefined): string {
  return String(value ?? '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');
}

export function isValidBic(value: string | null | undefined): boolean {
  const normalized = normalizeBic(value);
  return /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(normalized);
}

function firstValidIbanPrefix(compact: string): string | null {
  const max = Math.min(compact.length, 34);
  for (let len = max; len >= 15; len -= 1) {
    const candidate = compact.slice(0, len);
    if (isValidIban(candidate)) return candidate;
  }
  return null;
}

function findLabeledIban(text: string): string | null {
  const match = text.match(/IBAN[:\s]*([A-Z]{2}[\s\S]{10,60})/i);
  if (!match) return null;
  return firstValidIbanPrefix(normalizeIban(match[1]));
}

function findUnlabeledIban(text: string): string | null {
  const matches = String(text).match(/[A-Z]{2}\s*\d{2}(?:[\s-]*[A-Z0-9]){10,30}/gi) ?? [];
  for (const raw of matches) {
    const candidate = firstValidIbanPrefix(normalizeIban(raw));
    if (candidate) return candidate;
  }
  return null;
}

function findLabeledBic(text: string): string | null {
  const match = text.match(/\b(?:BIC|SWIFT)[:\s]*([A-Z]{4}\s*[A-Z]{2}\s*[A-Z0-9]{2}(?:\s*[A-Z0-9]{3})?)/i);
  if (!match) return null;
  const bic = normalizeBic(match[1]);
  return isValidBic(bic) ? bic : null;
}

/** Deterministic IBAN/BIC harvest from invoice text, including footer lines. */
export function extractPayeeBankFromText(text: string): { iban: string | null; bic: string | null } {
  const raw = String(text ?? '');
  const iban = findLabeledIban(raw) ?? findUnlabeledIban(raw);
  const bic = findLabeledBic(raw);
  return { iban, bic };
}
